/**
 * #733 slice 2. Whether the Live dock is still receiving Command Center state.
 *
 * The Cockpit refresh beat is the allowance. The shared terminal channel reports
 * closed and errored delivery through its existing transport states. Nothing here
 * starts a timer or a second subscription.
 */
import { durationPhrase } from './finished';
import type { TerminalTransportState } from './terminal-attachment';

/** Same beat as the Cockpit refresh. Not a new poll. */
export const LIVE_DOCK_FRESHNESS_ALLOWANCE_MS = 10_000;

export const LIVE_DOCK_PAUSED_TEXT = 'Live updates paused - reconnecting' as const;
export const LIVE_DOCK_STALE_ACTIVITY_LABEL = 'Reconnecting / stale' as const;

export interface LiveDockDeliveryFresh {
  readonly stale: false;
  readonly pausedText: null;
  readonly lastUpdatedText: null;
  readonly activityLabel: null;
}

export interface LiveDockDeliveryStale {
  readonly stale: true;
  readonly pausedText: typeof LIVE_DOCK_PAUSED_TEXT;
  readonly lastUpdatedText: string | null;
  readonly activityLabel: typeof LIVE_DOCK_STALE_ACTIVITY_LABEL;
}

export type LiveDockDelivery = LiveDockDeliveryFresh | LiveDockDeliveryStale;

/**
 * `fallback` is the existing transport state after CLOSED, CHANNEL_ERROR, or TIMED_OUT.
 * `reconnecting` is that same channel while its existing retry runs.
 * Live updates are paused when every subscribed channel is in one of those states.
 * One channel still live means delivery has not stopped. No channel yet is not a stop.
 */
export function liveDockChannelStopped(states: readonly TerminalTransportState[]): boolean {
  return states.length > 0 && states.every((state) => state === 'fallback' || state === 'reconnecting');
}

export function liveDockDelivery(input: {
  readonly nowMs: number;
  readonly lastFreshAt: number | null;
  /** When this view started waiting. Used only until the first fresh state arrives. */
  readonly awaitingSinceMs: number;
  readonly refreshIntervalMs: number;
  readonly channelClosedOrErrored: boolean;
}): LiveDockDelivery {
  const anchor = input.lastFreshAt ?? input.awaitingSinceMs;
  const waitingMs = Math.max(0, input.nowMs - anchor);
  const overdue = waitingMs > input.refreshIntervalMs;
  if (!input.channelClosedOrErrored && !overdue) {
    return { stale: false, pausedText: null, lastUpdatedText: null, activityLabel: null };
  }
  const sinceFresh = input.lastFreshAt === null ? null : Math.max(0, input.nowMs - input.lastFreshAt);
  return {
    stale: true,
    pausedText: LIVE_DOCK_PAUSED_TEXT,
    lastUpdatedText: sinceFresh === null ? null : `Last updated ${durationPhrase(sinceFresh)} ago`,
    activityLabel: LIVE_DOCK_STALE_ACTIVITY_LABEL,
  };
}
